import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { useState, useId } from 'react';
import type { InputVariant, InputSize, InputSurface } from './Input.css';
import {
  container,
  containerFullWidth,
  label as labelClass,
  labelUnderline,
  wrapper as wrapperClass,
  wrapperFullWidth,
  inputRecipe,
  iconBase,
  iconFocused,
  leftIconPositions,
  rightIconPositions,
  errorMessage as errorMessageClass,
} from './Input.css';

export type { InputVariant, InputSize, InputSurface };

/** Input props */
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Ref */
  ref?: Ref<HTMLInputElement>;
  /** 표면 축 — 기본 `glass`, 운영자 콘솔 절제는 `flat`. 검증 상태(`variant`)와 직교한다. */
  surface?: InputSurface;
  /** Input variant */
  variant?: InputVariant;
  /** Input size (renamed to avoid conflict with HTML size attribute) */
  inputSize?: InputSize;
  /** Full width input */
  fullWidth?: boolean;
  /** Left icon */
  leftIcon?: ReactNode;
  /** Right icon */
  rightIcon?: ReactNode;
  /** Error message to display below input */
  error?: string;
  /** Optional label above input */
  label?: string;
  /** Custom class name */
  className?: string;
}

export function Input({
  variant = 'default',
  surface = 'glass',
  inputSize = 'md',
  fullWidth = false,
  disabled = false,
  leftIcon,
  rightIcon,
  error,
  label,
  className,
  style,
  onFocus,
  onBlur,
  onClick,
  id: externalId,
  ref,
  ...props
}: InputProps) {
  const autoId = useId();
  const errorId = useId();
  const inputId = externalId ?? autoId;
  const [isFocused, setIsFocused] = useState(false);
  const effectiveVariant = error ? 'error' : variant;

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!disabled) setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    onClick?.(e);
  };

  const inputClass = inputRecipe({
    inputSize,
    variant: effectiveVariant,
    isFocused: isFocused && !disabled && !error,
    isDisabled: disabled,
    hasLeftIcon: !!leftIcon,
    hasRightIcon: !!rightIcon,
    surface,
  });

  const focused = isFocused && !disabled;

  return (
    <div className={`${container}${fullWidth ? ` ${containerFullWidth}` : ''}`}>
      {label && (
        <label htmlFor={inputId} className={labelClass}>
          {label}
          {/* 밑줄은 글래스의 장식이다 — 절제 표면에는 그리지 않는다(Select flat 과 같은 계약).
              `<label>` 의 콘텐츠 모델은 phrasing content 라 `<div>` 가 아니라 `<span>` 이다. */}
          {surface !== 'flat' && <span className={labelUnderline} />}
        </label>
      )}
      <div className={`${wrapperClass}${fullWidth ? ` ${wrapperFullWidth}` : ''}`}>
        {leftIcon && (
          <span
            className={`${iconBase}${focused ? ` ${iconFocused}` : ''} ${leftIconPositions[inputSize]}`}
          >
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          disabled={disabled}
          className={`${inputClass}${className ? ` ${className}` : ''}`}
          style={style}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={handleClick}
          data-surface={surface}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          {...props}
        />
        {rightIcon && (
          <span
            className={`${iconBase}${focused ? ` ${iconFocused}` : ''} ${rightIconPositions[inputSize]}`}
          >
            {rightIcon}
          </span>
        )}
      </div>
      {error && (
        <span id={errorId} role="alert" className={errorMessageClass}>
          {error}
        </span>
      )}
    </div>
  );
}
